import { action, observable, makeObservable, computed } from 'mobx'
import { required, validationContext } from 'validx'
import Job, { AmendmentVisibility } from 'jobs/Job'
import { maxLength } from 'utils/validation-utils'
import ModalViewModel from 'infra/ModalViewModel'
import JobStore from 'jobs/JobStore'
import { AlertFactory, AlertState } from 'alerts/Alert'
import { task } from 'mobx-task'
import { Id, Nullable } from 'types/definitions'
import { numberToDecimal } from 'utils/grpc-util'
import { toStream } from 'utils/mobx-util'

/**
 * Represents the shape of amendment visibility options.
 */
export interface IAmendmentVisibilityOption {
  displayName: string
  visibility: AmendmentVisibility
}

/**
 * View Model for the Create Amendment Dialog.
 */
export default class CreateAmendmentViewModel extends ModalViewModel {
  /**
   * Holds the maximun length value for the description field.
   */
  readonly MAX_DESCRIPTION_LENGTH = 500 as const

  /**
   * Holds the maximun length value for the internal note field.
   */
  readonly MAX_INTERNAL_NOTE_LENGTH = 250 as const

  /**
   *  Options for Amendment visibility selection.
   */
  visibilityOption: IAmendmentVisibilityOption[] = [
    { displayName: 'Public', visibility: AmendmentVisibility.Public },
    { displayName: 'Internal', visibility: AmendmentVisibility.Internal },
  ]

  /**
   * Description of the amendment.
   */
  @observable description = ''

  /**
   * Internal note of the amendment.
   */
  @observable internalNote = ''

  /**
   * Visibility of the amendment.
   */
  @observable visibility: Nullable<AmendmentVisibility> = null

  /**
   * Amendment Client amount.
   */
  @observable clientAmount = 0

  /**
   * Amendment Provider amount.
   */
  @observable providerAmount = 0

  /**
   * Validation context for this view model.
   */
  readonly validation = validationContext(this, {
    description: [
      required(
        'Description is required, the Client/Pro should know what they are paying/getting paid for.'
      ),
      maxLength(
        this.MAX_DESCRIPTION_LENGTH,
        'Amendment description must be less than or equal to 500 characters.'
      ),
    ],
    internalNote: [
      required('Internal note is required.'),
      maxLength(
        this.MAX_INTERNAL_NOTE_LENGTH,
        'Amendment internal note must be less than or equal to 250 characters.'
      ),
    ],
    visibility: [required('Visibility is required.')],
  })

  constructor(
    private readonly job: Job,
    private readonly jobStore: JobStore,
    private readonly newAlert: AlertFactory
  ) {
    super()

    makeObservable(this)

    this.close$.subscribe(this.reset)

    // Clear field validations on change
    this._clearValidationOnChange('description')
    this._clearValidationOnChange('internalNote')
    this._clearValidationOnChange('visibility')
  }

  /**
   * The currently selected amendment visibility option.
   */
  @computed
  get option() {
    return this.visibilityOption.find((o) => o.visibility === this.visibility)
  }

  /**
   * Sets the amendment visibility.
   */
  @action.bound
  setVisibilityOption(visibilityOption?: IAmendmentVisibilityOption) {
    if (visibilityOption) {
      this.visibility = visibilityOption.visibility
    }
  }

  /**
   * Clears the fields of the View Model.
   */
  @action.bound
  reset() {
    this.description = ''
    this.internalNote = ''
    this.clientAmount = 0
    this.providerAmount = 0
    this.visibility = null

    this.createAmendment.reset()
    this.validation.reset()
  }

  /**
   * Indicates if the View Model is valid.
   */
  @action.bound
  validate() {
    return this.validation.reset().validate().isValid
  }

  /**
   * Creates an amendment.
   */
  createAmendment = task.resolved(async () => {
    const alert = this.newAlert()

    if (!this.job.canViewAmendments) {
      alert
        .withState(AlertState.Error)
        .saying('You do not have permission to perform this action.')
        .autoDismiss()
      return
    }

    if (!this.validate()) return

    if (this.visibility == null) return

    try {
      alert
        .withState(AlertState.Pending)
        .saying('Creating amendment', `Adding amendment to ${this.job.name}...`)
        .autoDismiss()
        .show()

      await this.jobStore.createAmendment(
        Id.assert(this.job.id),
        this.description,
        this.internalNote,
        this.visibility,
        numberToDecimal(this.clientAmount),
        numberToDecimal(this.providerAmount)
      )
    } catch (error) {
      alert.fromError(error).autoDismiss()

      throw error
    }

    alert
      .withState(AlertState.Success)
      .saying('Success!', `The amendment was added to ${this.job.name}.`)
      .autoDismiss()

    this.close()
  })

  /**
   * Shows add amendment dialog.
   */
  show() {
    if (this.showing) {
      return
    }

    super.show()
  }

  /**
   * Sets up automatic clearing of validation errors when the field changes.
   * @param field
   * @private
   */
  private _clearValidationOnChange(field: keyof this) {
    toStream(() => this[field])
      .pipe(this.whileShowing())
      .subscribe(() => this.validation.clearErrors(field as string))
  }
}
